/**
 * useEntityPositions - Tracks DOM positions of entity cards for connection lines
 *
 * Features:
 * - ResizeObserver for responsive position updates
 * - Connection dot refs for precise line endpoints
 * - Recalculation on layout changes
 * - State-based position storage for reactive updates
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  EntityPositionMap,
  Point,
  UseEntityPositionsReturn,
} from '../types/linking-view.types';

/**
 * Hook for tracking entity card positions
 */
export function useEntityPositions(): UseEntityPositionsReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const connectionDotRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [positionMap, setPositionMap] = useState<EntityPositionMap>({});
  // Version counter to force re-renders when positions change
  const [, setVersion] = useState(0);

  // Register a connection dot element
  const registerConnectionDot = useCallback(
    (entityId: string, element: HTMLDivElement | null) => {
      if (element) {
        connectionDotRefs.current.set(entityId, element);
      } else {
        connectionDotRefs.current.delete(entityId);
      }
      // Trigger recalculation after registration
      requestAnimationFrame(() => {
        setVersion((v) => v + 1);
      });
    },
    [],
  );

  // Get the connection point position relative to the linking container
  const getConnectionPoint = useCallback((entityId: string): Point | null => {
    const dotElement = connectionDotRefs.current.get(entityId);

    if (!dotElement) return null;

    // Find the linking container (marked with data-linking-container)
    const container = dotElement.closest('[data-linking-container]');
    if (!container) return null;

    const dotRect = dotElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Center of the dot element, relative to the linking container
    return {
      x: dotRect.left - containerRect.left + dotRect.width / 2,
      y: dotRect.top - containerRect.top + dotRect.height / 2,
    };
  }, []);

  // Recalculate all positions
  const recalculatePositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the linking container
    const linkingContainer =
      container.querySelector('[data-linking-container]') || container;
    const containerRect = linkingContainer.getBoundingClientRect();
    const newPositions: EntityPositionMap = {};

    connectionDotRefs.current.forEach((element, entityId) => {
      const rect = element.getBoundingClientRect();
      newPositions[entityId] = {
        top: rect.top - containerRect.top,
        left: rect.left - containerRect.left,
        right: rect.right - containerRect.left,
        bottom: rect.bottom - containerRect.top,
        width: rect.width,
        height: rect.height,
      };
    });

    setPositionMap(newPositions);
    setVersion((v) => v + 1);
  }, []);

  // Set up ResizeObserver for container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial calculation after a short delay to ensure layout is complete
    const initialTimer = setTimeout(() => {
      recalculatePositions();
    }, 100);

    // Watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      recalculatePositions();
    });

    resizeObserver.observe(container);

    // Also recalculate on scroll
    const handleScroll = () => {
      recalculatePositions();
    };
    window.addEventListener('scroll', handleScroll, true);

    // Watch for layout changes
    const mutationObserver = new MutationObserver(() => {
      // Debounce the recalculation
      requestAnimationFrame(recalculatePositions);
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [recalculatePositions]);

  return {
    containerRef,
    positionMap,
    connectionDotRefs: connectionDotRefs.current,
    registerConnectionDot,
    getConnectionPoint,
    recalculatePositions,
  };
}

export default useEntityPositions;
